import React, {useMemo} from 'react';
import moment from 'moment';
import SprintBars from "./SprintBars";
import {useSelector} from "react-redux";
import {Sprint} from "../../../../services/organization";

interface CalendarDateProps {
  duration: number;
  weekdaysArray?: string[];
  boxWidth: number;
  leftBoxWidth: number;
}

const CalendarDate: React.FC<CalendarDateProps> = ({ 
  duration, 
  weekdaysArray,
  boxWidth,
  leftBoxWidth,
}) => {
  const {
    teamSprints = [] as Sprint[]
  } = useSelector((state: any) => state.resources);

  const dates = [...new Array(duration)];

  // Group dates by month for month headers
  const datesByMonth = weekdaysArray ? weekdaysArray.reduce((acc: { [key: string]: string[] }, date) => {
    const monthYear = moment(date, 'DD MMM YYYY').format('MMM YYYY');
    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(date);
    return acc;
  }, {}) : {};

  // Build date->index map for sprint calculations
  const dateToIndex = useMemo(() => {
    const m = new Map<string, number>();
    if (Array.isArray(weekdaysArray)) {
      weekdaysArray.forEach((d, i) => {
        if (d) {
          const k = moment(d, 'DD MMM YYYY').format('YYYY-MM-DD');
          m.set(k, i);
        }
      });
    }
    return m;
  }, [weekdaysArray]);

  // Calculate sprint spans for bars
  const sprintSpans = useMemo(() => {
    if (!teamSprints?.length || !weekdaysArray?.length) return [];
    
    const sortedSprints = [...teamSprints]
      .filter(s => s.start_date && s.end_date) // Filter null dates before sorting
      .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)));
    
    return sortedSprints
      .map(s => {
        const startISO = moment(s.start_date).format('YYYY-MM-DD');
        const endISO = moment(s.end_date).format('YYYY-MM-DD');
        
        // Find start index
        let startIdx = dateToIndex.get(startISO);
        if (typeof startIdx !== 'number') {
          // If sprint starts before view, clamp to first visible date
          const firstISO = moment(weekdaysArray[0], 'DD MMM YYYY').format('YYYY-MM-DD');
          if (moment(startISO).isBefore(firstISO) && moment(endISO).isSameOrAfter(firstISO)) {
            startIdx = 0;
          }
        }
        
        // Find end index
        let endIdx = dateToIndex.get(endISO);
        if (typeof endIdx !== 'number') {
          // If sprint ends after view, clamp to last visible date
          const lastISO = moment(weekdaysArray[weekdaysArray.length - 1], 'DD MMM YYYY').format('YYYY-MM-DD');
          if (moment(endISO).isAfter(lastISO) && moment(startISO).isSameOrBefore(lastISO)) {
            endIdx = weekdaysArray.length - 1;
          }
        }
        
        // Only return valid spans
        if (typeof startIdx !== 'number' || typeof endIdx !== 'number' || endIdx < startIdx) {
          return null;
        }
        
        return { sprint: s, startISO, endISO, startIdx, endIdx };
      })
      .filter(Boolean) as any[];
  }, [teamSprints, weekdaysArray, dateToIndex]);

  return (
    <div className='flex flex-row sticky top-0 z-30' style={{ width: `${dates.length * boxWidth}px` }}>
      {/* RESOURCES Header - Sticky Left */}
      <div id="resources-text" className="sticky shadow-r-xl left-0 z-30 flex items-center justify-center bg-light-surfacePrimary dark:bg-secondary-dark border-r-2 rounded-tr-2xl border-t border-b border-gray-300 dark:border-[#374151] text-gray-700 dark:text-white"
        style={{
          minWidth: leftBoxWidth + 1,
          height: `calc(36px + 45px + ${sprintSpans.length > 0 ? '32px' : '0px'})`,
          boxShadow: '4px 0 8px rgba(0,0,0,0.1)',
        }}>
        <div className="text-sm font-medium uppercase tracking-wide">
          PEOPLE
        </div>
      </div>

      <div className='flex flex-col'>
        {/* Months Header Row */}
        <div style={{ display: 'flex', }}>
          {/* Month headers */}
          <div className="flex bg-white dark:bg-[#1a1f2e]">
            {Object.entries(datesByMonth).map(([monthYear, monthDates], monthIndex) => {
              const monthWidth = monthDates.length * boxWidth;
              const isEvenIndex = monthIndex % 2 === 0;

              return (
                <div
                  key={monthYear}
                  className={`flex items-center justify-center relative border-b border-r ${isEvenIndex
                      ? 'bg-gray-50 dark:bg-[#111928]'
                      : 'bg-gray-100 dark:bg-[#151D2E]'
                    } text-gray-700 dark:text-white border-gray-200 dark:border-[#374151]`}
                  style={{
                    width: monthWidth,
                    minWidth: monthWidth,
                    height: '36px',
                  }}
                >
                  <span className="text-sm font-medium">
                    {monthYear}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Day Headers Row */}
        <div style={{ display: 'flex', }}>
          {/* Day headers - inline with RESOURCES */}
          <div
            className="flex bg-white dark:bg-[#1a1f2e]"
            style={{ 
              width: `${duration * boxWidth}px`, 
              zIndex: 20,
              height: '45px',
            }}
          >
            {dates.map((date, index) => {
              const currentDate = weekdaysArray ? weekdaysArray[index] : '';
              const dayName = currentDate ? moment(currentDate, 'DD MMM YYYY').format('ddd') : '';
              const dayNumber = currentDate ? moment(currentDate, 'DD MMM YYYY').format('D') : '';
              const isToday = currentDate ? moment(currentDate, 'DD MMM YYYY').isSame(moment(), 'day') : false;

              return (
                <div
                  key={index}
                  className={`flex flex-col items-center justify-center relative border-b border-gray-300 dark:border-[#2d3748] border-r border-r-gray-300 dark:border-r-[#2d3748] ${isToday ? 'bg-green-50 dark:bg-[#1b3333]' : ''}`}
                  style={{
                    width: `${boxWidth}px`,
                    minWidth: `${boxWidth}px`,
                    height: '45px'
                  }}>
                  {/* Day number */}
                  <span className={`text-xs text-gray-700 ${isToday ? 'text-green-700 dark:text-green-400' : ''}`}>
                    {dayNumber}
                  </span>
                  {/* Day name */}
                  <span className={`text-xs text-gray-500 ${isToday ? 'text-green-700 dark:text-green-400' : ''} mt-1 uppercase`}>
                    {dayName}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sprint Headers Row */}
        {sprintSpans.length > 0 && (
          <div style={{ display: 'flex' }}>
            <div className="relative" style={{ width: `${duration * boxWidth}px`, height: '32px' }}>
              <SprintBars spans={sprintSpans} boxWidth={boxWidth} duration={duration} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarDate; 